import queryClient, { IQueryParams } from 'queries';
import helpers from 'helpers/index';
import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { TypedUser } from 'interface/user.model';
import { TypedOrders } from './orders.query';
import { TypedVehicle } from './vehicle.query';
import { TypedInvoices } from './invoices.query';




/** Công nợ hay không? Default là mặc định, không công nợ ... */
export enum ENUM_TRANSACTION_PAYMENT_METHOD {
    CASH = 'CASH',
    BANK = 'BANK',
    DEBT_CUSTOMER = 'DEBT_CUSTOMER',
    DEBT_AGENT = 'DEBT_AGENT',
    THIRD_PARTY = 'THIRD_PARTY',
}

export enum ENUM_TRANSACTION_STATUS {
    NORMAL = 'NORMAL',
    REFUND = 'REFUND'
}




export interface TypedTransactions {
    id?: string;
    is_from_qr?: number;
    order_id?: string;
    amount?: number;
    payment_method?: ENUM_TRANSACTION_PAYMENT_METHOD;
    payment_status?: ENUM_TRANSACTION_STATUS;
    description?: string;
    casher?: string;
    driver_id?: string;
    invoice_id?: string;
    vehicle_id?: string;
    createdAt?: string;

    refund_note?: string;
    refund_by?: bigint;
    refunded_at?: bigint;

    debt_cleared_by?: string;
    debt_cleared_at?: string;
    debt_clear_note?: string;

    casher_data?: TypedUser;
    debt_clearer?: TypedUser;
    refunder_data?: TypedUser;

    order_data?: TypedOrders;

    vehicle_data?: TypedVehicle;
    driver_data?: TypedUser;

    invoices?: TypedInvoices;

    /** Bộ lọc ngày đi của đơn, truyền vào là được */
    "order_data.departure_date"?: string; /** Bigint trong csdl, coi nó là string */

}


export interface TypedTransactions_callback {
    id?: string;
    amount?: number;
    content?: string;
    timestamp?: string;
    from_bank?: string;
    from_name?: string;
    bank_order_id?: string;
    is_valid?: number;
    note: string;
    createdAt?: string;
}

export interface TypedAgribankCallbackPayload {
    amount: number;
    content: string;
    timestamp: number;
    from: string;
    from_name: string;
}


export interface TypedTransactionAnalytics {
    "count": { "CASH": number, "BANK": number, DEBT_CUSTOMER: number, DEBT_AGENT: number, THIRD_PARTY: number, REFUND: number }, "sum": { "CASH": number, "BANK": number, DEBT_CUSTOMER: number, DEBT_AGENT: number, THIRD_PARTY: number, REFUND: number }
}

type IQuery = TypedTransactions & IQueryParams;
type IQuery2 = TypedTransactions_callback & IQueryParams;

/*
* List of all transactions
*/

export function useGetTransactions(object: IQuery) {
    const EndURL = helpers.buildEndUrl(object);
    return useQuery({
        queryKey: ["transactions/fetch_entity_list", object],
        queryFn: () => axios.get<{
            transactions: TypedTransactions[];
            analytics: TypedTransactionAnalytics;
            totalItems: number;
        }>(`/transactions/${EndURL}`).then(response => {
            let { data, headers } = response;
            return {
                transactions: data.transactions,
                analytics: data.analytics,
                totalItems: Number(headers['x-total-count'] || 0)
            }
        }),
        retry: 1,
    });
}
export function useGetTransaction(transaction_id: any) {
    return useQuery({
        queryKey: ["transactions/fetch_entity", transaction_id],
        queryFn: () => axios.get<TypedTransactions>(`/transactions/${transaction_id}`).then(response => response.data),
        retry: 1,
        enabled: !!transaction_id
    });
}

/** Tải về excel bản đang xem */
export function useDownloadTransactions() {
    return useMutation({
        mutationKey: ["transactions/download"],
        mutationFn: (object: IQuery) => axios.get<Blob>(`/transactions/download/${helpers.buildEndUrl(object)}`, { responseType: 'blob', }).then(response => response.data),
    })
}


export function useMutateUpdateTransaction() {
    return useMutation({
        mutationKey: ["transactions/update_entity"],
        mutationFn: ({ id, ...__object }: TypedTransactions) => axios.patch<TypedTransactions>(`/transactions/${id}`, __object).then(response => response.data),
        retry: 1,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['transactions/fetch_entity_list'] });
            queryClient.invalidateQueries({ queryKey: ['transactions/fetch_entity'] });
        }
    });
}


/*
* List of all transactions
*/
export function useGetTransactionsCallback(object: IQuery2) {
    const EndURL = helpers.buildEndUrl(object);
    return useQuery({
        queryKey: ["transactions_callback/fetch_entity_list", object],
        queryFn: () => axios.get<TypedTransactions_callback[]>(`/transactions_callback/${EndURL}`).then(response => {
            let { data, headers } = response;
            return {
                entities: data,
                totalItems: Number(headers['x-total-count'] || 0)
            }
        }),
        retry: 1,
    });
}




export function useMutateUpdateTransactionCallback() {
    return useMutation({
        mutationKey: ["transactions_callback/update_entity"],
        mutationFn: ({ id, ...__object }: TypedTransactions_callback) => axios.patch<TypedTransactions_callback>(`/transactions_callback/${id}`, __object).then(response => response.data),
        retry: 1,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['transactions_callback/fetch_entity_list'] });
        }
    });
}
export function useMutateResyncTransactionCallback() {
    return useMutation({
        mutationKey: ["transactions_callback/resync"],
        mutationFn: (transaction_callback_id) => axios.post<TypedTransactions_callback>(`/transactions_callback/refresh/${transaction_callback_id}`).then(response => response.data),
        retry: 1,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['transactions_callback/fetch_entity_list'] });
        }
    });
}

export function useMutateCreateAgribankCallback() {
    return useMutation({
        mutationKey: ["transactions_callback/create_agribank_callback"],
        mutationFn: (payload: TypedAgribankCallbackPayload) => axios.post<TypedTransactions_callback>(`/agribank_callback`, payload).then(response => response.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['transactions_callback/fetch_entity_list'] });
        }
    });
}


type QRCodeTypedResponse = {
    qr_code_data: string;
    timestamp: string;
}

export function useMutateGetQRImage() {
    return useMutation({
        mutationKey: ['qrcode/fetch_qr_image'],
        mutationFn: ({ order_id, amount }: { order_id: string; amount: number }) => {
            return axios.get<QRCodeTypedResponse>(`/transactions/qrcode_payment_web/${order_id}/${amount}`).then((response) => response.data);
        },
    })
};



/** Gạt nợ công nợ */
export function useMutateRemoveDebitClearing() {
    return useMutation({
        mutationKey: ["transactions/debt_cleared"],
        mutationFn: ({ debt_clear_note, payment_method, transaction_id }: { debt_clear_note: string, payment_method: string, transaction_id: any[] }) => axios.post<any>(`/transactions/debt_cleared/`, { debt_clear_note, payment_method, transaction_id }).then(response => response.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['transactions/fetch_entity_list'] });
        }
    });
}



/** Đánh dấu hoàn tiền */
export function useMutateRefund() {
    return useMutation({
        mutationKey: ["transactions/refund"],
        mutationFn: ({ refund_note, transaction_id }: { refund_note: string, transaction_id: any[] }) => axios.post<any>(`/transactions/refund/`, { refund_note, transaction_id }).then(response => response.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['transactions/fetch_entity_list'] });
        }
    });
}

/** xóa một giao dịch */
export function useMutateDeleteTransaction() {
    return useMutation({
        mutationKey: ["transactions/delete_entity"],
        mutationFn: (transaction_id: any) => axios.delete<any>(`/transactions/${transaction_id}`).then(response => response.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['transactions/fetch_entity_list'] });
            queryClient.invalidateQueries({ queryKey: ['orders/fetch_entity'] });
        }
    });
}
